import {
    AnimationClip,
    Euler,
    InterpolateLinear,
    InterpolateSmooth,
    Quaternion,
    QuaternionKeyframeTrack,
    Vector3,
    VectorKeyframeTrack,
} from 'three'

export const arraysToVector3s = (vectorArray: number[]) =>
    new Vector3(...vectorArray)

export const eulerArrayToQuaternionArray = (
    eulerArray: EulerArray
): QuaternionArray => {
    const e = new Euler(...eulerArray)
    const q = new Quaternion().setFromEuler(e)
    return [q.x, q.y, q.z, q.w]
}

export const createAnimationClip = (
    name: string,
    keyframes: ObjectKeyframes
) => {
    const { scale, position, rotation } = keyframes
    const kftArray = []
    if (scale) {
        const scaleTimes = scale.map(({ time }) => time)
        const scaleValues = scale.reduce(
            (acc: number[], { value }) => acc.concat([value, value, value]),
            []
        )
        const keyframeTrack = new VectorKeyframeTrack(
            '.scale',
            scaleTimes,
            scaleValues
        )
        kftArray.push(keyframeTrack)
    }

    if (position) {
        const positionTimes = position.map(({ time }) => time)
        const positionValues = position.reduce(
            (acc: number[], { value }) => acc.concat(value),
            []
        )
        const keyframeTrack = new VectorKeyframeTrack(
            '.position',
            positionTimes,
            positionValues,
            InterpolateSmooth
        )
        kftArray.push(keyframeTrack)
    }

    if (rotation) {
        const rotationTimes = rotation.map(({ time }) => time)
        const rotationValues = rotation.reduce(
            (acc: number[], { value }) =>
                acc.concat(eulerArrayToQuaternionArray(value)),
            []
        )
        const keyframeTrack = new QuaternionKeyframeTrack(
            '.quaternion',
            rotationTimes,
            rotationValues,
            InterpolateLinear
        )
        kftArray.push(keyframeTrack)
    }

    // pass -1 to AnimationClip for it to auto-detect duration of keyframes
    return new AnimationClip(name, 1, kftArray)
}

type Vector3Array = [number, number, number]
type EulerArray = [number, number, number]
type QuaternionArray = [number, number, number, number]

export interface ScaleKeyframe {
    time: number
    value: number
}

export interface Vector3Keyframe {
    time: number
    value: Vector3Array
}

export interface EulerKeyframe {
    time: number
    value: EulerArray
}

interface ObjectKeyframes {
    scale?: ScaleKeyframe[]
    position?: Vector3Keyframe[]
    rotation?: Vector3Keyframe[]
}
